/** @format */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {ApolloProvider} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {ExecutiveReport, PublicReportGate} from 'ui';

import {ReportUnavailable} from '../Report/components/ReportUnavailable';
import {apolloErrorMessage} from '../Report/helpers/apolloErrorMessage';
import {toDataDate} from '../Report/helpers/toDataDate';
import {toReportData} from '../Report/helpers/toReportData';
import {
  createPublicApolloClient,
  forgetPublicReportToken,
  writePublicReportToken,
} from './apolloClient';
import {
  useOpenPublicProjectReportMutation,
  usePublicProjectReportQuery,
} from './graphql/__generated__';

/**
 * Отчёт по публичной ссылке.
 *
 * Сюда приходит человек без доступа в систему. Ни один его запрос не
 * должен коснуться клиента кабинета, поэтому маршрут подставляет свой
 * `ApolloProvider` — React берёт ближайший, и внешний остаётся недоступен.
 */
const PublicReport = () => {
  const {slug = ''} = useParams();
  const client = useMemo(() => createPublicApolloClient(slug), [slug]);

  return (
    <ApolloProvider client={client}>
      <PublicReportContent slug={slug} />
    </ApolloProvider>
  );
};

/**
 * Содержимое отдельно от обёртки не ради красоты: `ApolloProvider` внутри
 * компонента перекрывает любой провайдер снаружи, и `MockedProvider` в
 * тесте до хуков не достучался бы.
 */
export const PublicReportContent = ({slug}: {slug: string}) => {
  const {t} = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = usePublicProjectReportQuery({
    variables: {slug},
  });
  const [openReport, {loading: opening}] = useOpenPublicProjectReportMutation();

  /*
   * Гард от повторной отправки синхронный, как и у владельца.
   *
   * `opening` отстаёт на рендер и падает в `false`, пока `refetch()` ещё в
   * полёте, а гейт всё это время на экране: нетерпеливый гость жмёт «Открыть
   * отчёт» ещё раз и тратит вторую попытку против рейт-лимита — с ВЕРНЫМ
   * паролем запирая себя сам.
   */
  const submitBusyRef = useRef(false);

  const handleSubmit = useCallback(
    async (password: string) => {
      if (submitBusyRef.current) return;
      submitBusyRef.current = true;
      setError(null);
      try {
        const result = await openReport({variables: {slug, password}});
        const token = result.data?.openPublicProjectReport.token;
        if (!token) return;

        if (!writePublicReportToken(slug, token)) {
          // Пароль подошёл, но положить доступ некуда: браузер запретил
          // данные сайта. Молчать нельзя — иначе повторный запрос уйдёт
          // без заголовка, гость снова увидит форму и решит, что ошибся
          // паролем, хотя пароль верный.
          setError(
            t(
              'Пароль подошёл, но браузер не разрешил сохранить доступ. Откройте ссылку в обычном окне.',
              'Пароль подошёл, но браузер не разрешил сохранить доступ. Откройте ссылку в обычном окне.',
            ),
          );
          return;
        }

        // Заголовок линк соберёт заново — на момент отправки токен уже на
        // месте. `refetchQueries` не используем: клиент на `no-cache`.
        await refetch();
      } catch (cause) {
        setError(
          apolloErrorMessage(cause, t('Не удалось открыть отчёт', 'Не удалось открыть отчёт')),
        );
      } finally {
        submitBusyRef.current = false;
      }
    },
    [openReport, refetch, slug, t],
  );

  /*
   * Транспортный отказ забывает токен.
   *
   * Иначе вкладка запирается навсегда: негодный токен уходит и с первым
   * запросом после перезагрузки, отказ воспроизводится, и форма пароля
   * становится недостижимой. Ценой служит повторный ввод пароля после
   * сетевой заминки — это дешевле тупика.
   */
  useEffect(() => {
    if (queryError) forgetPublicReportToken(slug);
  }, [queryError, slug]);

  const access = data?.publicProjectReport.access;
  const snapshot = data?.publicProjectReport.snapshot;

  // Приведение стоит дорого (снимок обходит типограф целиком), считаем
  // один раз на снимок.
  const reportData = useMemo(
    () => (snapshot?.reportData ? toReportData(snapshot.reportData) : null),
    [snapshot?.reportData],
  );

  // Гард держит лоадер только до первого ответа — `data`/`access` до него
  // не определены, и без него выполнение упало бы в ветку гейта раньше
  // времени. От того, эмитит ли Apollo промежуточные состояния `loading`
  // на повторном `refetch()` после обмена пароля, гард не зависит: `!data`
  // отсекает их в любом случае, если они вообще случаются.
  if (loading && !data) {
    return <ReportUnavailable exits={false} title={t('Загружаем отчёт', 'Загружаем отчёт')} />;
  }

  // Технический сбой (недоступный API, 500, отвергнутый CORS-preflight) не
  // должен показываться гостю формой пароля: пароль здесь ни при чём, и
  // верный пароль на сломанном запросе привёл бы к бесконечному циклу.
  if (queryError && !data) {
    return (
      <ReportUnavailable
        exits={false}
        title={t('Не удалось открыть отчёт', 'Не удалось открыть отчёт')}
        detail={t(
          'Возможно, дело в соединении. Обновите страницу или попробуйте открыть ссылку позже.',
          'Возможно, дело в соединении. Обновите страницу или попробуйте открыть ссылку позже.',
        )}
      />
    );
  }

  if (access === 'NOT_FOUND') {
    return <PublicReportGate revoked />;
  }

  if (access !== 'OPEN') {
    return <PublicReportGate onSubmit={handleSubmit} error={error} busy={opening} />;
  }

  if (!snapshot || !reportData) {
    return (
      <ReportUnavailable
        exits={false}
        title={t('Содержимое отчёта недоступно', 'Содержимое отчёта недоступно')}
        detail={t(
          'Отчёт открылся, но его содержимое не пришло. Попробуйте позже или попросите прислать ссылку заново.',
          'Отчёт открылся, но его содержимое не пришло. Попробуйте позже или попросите прислать ссылку заново.',
        )}
      />
    );
  }

  // Ни `onLogoClick`, ни пропов панели: у гостя нет ни кабинета, куда
  // уводить, ни права управлять ссылкой.
  return (
    <ExecutiveReport
      data={reportData}
      dataDate={toDataDate(snapshot.generatedAt, snapshot.periodEnd)}
    />
  );
};

export default PublicReport;
